import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle, Delete as DeleteIcon, ExpandMore, RestartAlt, WarningAmber } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  FormControlLabel,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  CompanyDailyNavConfigVersion,
  DailyNavSetupCompany,
  FirmDailyNavCompanyConfigRow,
} from 'api';
import {
  DAILY_NAV_THRESHOLD_KEYS,
  dailyNavDateLabel,
  fractionToPercent,
  getDailyNavThresholdGroups,
  isValidThresholdPercent,
} from 'common/constants/dailyNav';
import {
  capIqSelectionToCompRows,
  COMP_TYPE_LABELS,
  compListsDiffer,
  gpcApproachToCompRows,
  toCompRow,
} from 'common/constants/dailyNavComps';
import {
  areOverridesValid,
  overridesDiffer,
  overridesFromConfig,
  overridesToPayload,
  ThresholdOverrides,
} from 'common/constants/dailyNavOverrides';
import { CurrentCompanyMeasurementDate } from 'common/types/measurementDate';
import AlexandriaCompanyPicker from 'components/AlexandriaCompanyPicker';
import { GridSkeleton } from 'components/Grid';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Input,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  Text,
} from 'components/ui';
import {
  ADD_COMP_OR_ETF_BUTTON_LABEL,
  ADD_PUBLIC_OR_ETF_DIALOG_TITLE,
} from 'pages/Portfolio/pages/CompanySummary/components/DailyMonitoring/constants';
import AddGpcDialog from 'pages/Valuations/approaches/guidelinePublicCompanies/AddGpcDialog';
import { CapIQCompanyData } from 'pages/ValuationsAllocation/types';
import {
  useCompsWatchlist,
  useGetCompanyDailyNavConfig,
  useUpdateCompanyDailyNavConfig,
} from 'services/hooks/dailyNav';
import { dbShortDate } from 'utilities/dateFormats';
import { DAILY_NAV_SETUP_TEST_IDS } from '../DailyNavSetup.testIds';
import { PreviousValuation, SetupDataState, SetupMeasurementDate } from '../dailyNavSetup.utils';
import CompanyWalkthroughProgress from './CompanyWalkthroughProgress';
import LeaveWithUnsavedChangesDialog from './LeaveWithUnsavedChangesDialog';
import { useLeaveGuard } from '../hooks/useLeaveGuard';

type CompanySetupStepProps = {
  firmId: number;
  company: FirmDailyNavCompanyConfigRow;
  setup?: DailyNavSetupCompany;
  setupState: SetupDataState;
  // The server's reason when the setup data failed to load, e.g. a calendar problem.
  setupErrorMessage?: string;
  disabled: boolean;
  // Leaves for the company list without saving.
  onBack: () => void;
  // Single-company mode: after a save, back to the list.
  onSaved: () => void;
  // Every successful save, for Review's list of this session's changes.
  onConfigSaved?: (before: CompanyDailyNavConfigVersion | null, after: CompanyDailyNavConfigVersion) => void;
  // Walkthrough mode: every company in order, saving before each move.
  walkthrough?: {
    companies: FirmDailyNavCompanyConfigRow[];
    onNavigate: (companyId: number) => void;
    onFinish: () => void;
  };
};

// Numbered, and outlined when it is the thing still waiting on the user.
const Section = ({
  number,
  title,
  isPending,
  isDone,
  testId,
  children,
}: {
  number: number;
  title: string;
  isPending: boolean;
  isDone: boolean;
  testId: string;
  children: ReactNode;
}) => (
  <Paper
    variant="outlined"
    data-testid={testId}
    sx={{ p: 3, borderWidth: isPending ? 2 : 1, borderColor: isPending ? 'primary.main' : 'divider' }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
      <Text variant="h4">
        {number}. {title}
      </Text>
      {isDone && <CheckCircle color="success" fontSize="small" />}
    </Stack>
    {children}
  </Paper>
);

const CompanySetupStep = ({
  firmId,
  company,
  setup,
  setupState,
  setupErrorMessage,
  disabled,
  onBack,
  onSaved,
  onConfigSaved,
  walkthrough,
}: CompanySetupStepProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const companyId = company.company_id;
  const {
    data: currentConfig,
    isLoading,
    refetch: refetchConfig,
  } = useGetCompanyDailyNavConfig(firmId, companyId);
  const { updateCompanyDailyNavConfig, isPending } = useUpdateCompanyDailyNavConfig();

  const companyConfig = currentConfig?.company_config ?? null;
  const previousValuation = (setup?.previous_valuation ?? null) as PreviousValuation;
  // Cap IQ searches as of the previous valuation, else the company's latest measurement date.
  const searchDate = previousValuation ?? ((setup?.latest_measurement_date ?? null) as SetupMeasurementDate);
  const gpcApproaches = useMemo(() => setup?.gpc_approaches ?? [], [setup?.gpc_approaches]);

  const [monitoringEnabled, setMonitoringEnabled] = useState(true);
  const [alexandriaId, setAlexandriaId] = useState<string | null>(null);
  const { comps, resetComps, addComps, removeComp, isResolvingLinks } = useCompsWatchlist(firmId, companyId);
  const [selectedGpcApproachId, setSelectedGpcApproachId] = useState<number | ''>('');
  const [saveTrigger, setSaveTrigger] = useState<'previous' | 'primary'>('primary');
  const [overrides, setOverrides] = useState<ThresholdOverrides>(() => overridesFromConfig(null));

  const draftDiffersFrom = (config?: CompanyDailyNavConfigVersion | null) =>
    overridesDiffer(config, overrides) ||
    (config?.monitoring_enabled ?? true) !== monitoringEnabled ||
    (config?.alexandria_id ?? null) !== alexandriaId ||
    compListsDiffer((config?.comps ?? []).map(toCompRow), comps);

  // What the draft was seeded from: a background refetch (another tab's save, a firm
  // cascade) must not replace edits made against it.
  const seededFromRef = useRef<CompanyDailyNavConfigVersion | null | undefined>(undefined);
  const reseedNextRef = useRef(false);

  useEffect(() => {
    if (!currentConfig) {
      return;
    }
    const hasSeeded = seededFromRef.current !== undefined;
    if (hasSeeded && !reseedNextRef.current && draftDiffersFrom(seededFromRef.current)) {
      return;
    }
    reseedNextRef.current = false;
    seededFromRef.current = currentConfig.company_config ?? null;
    setMonitoringEnabled(currentConfig.company_config?.monitoring_enabled ?? true);
    setAlexandriaId(currentConfig.company_config?.alexandria_id ?? null);
    resetComps((currentConfig.company_config?.comps ?? []).map(toCompRow));
    setOverrides(overridesFromConfig(currentConfig.company_config));
    // Reads the draft to decide, but only a new config should trigger it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConfig, resetComps]);

  const firmConfig = currentConfig?.firm_config ?? null;
  // Windows are firm-level, so the labels follow the firm config this company anchors to.
  const thresholdGroups = useMemo(
    () => getDailyNavThresholdGroups(firmConfig?.short_window_sessions, firmConfig?.long_window_sessions),
    [firmConfig?.short_window_sessions, firmConfig?.long_window_sessions]
  );
  const overrideCount = DAILY_NAV_THRESHOLD_KEYS.filter(key => overrides[key] !== '').length;
  const isOverridesValid = areOverridesValid(overrides);

  // Preselect the approach, but never copy it: the user chooses what to add.
  useEffect(() => {
    setSelectedGpcApproachId(gpcApproaches.length === 1 ? gpcApproaches[0].gpc_approach_id : '');
  }, [gpcApproaches]);

  const savedAlexandriaId = companyConfig?.alexandria_id ?? null;
  const isAlexandriaConfirmed = Boolean(savedAlexandriaId) && savedAlexandriaId === alexandriaId;
  const isDirty = draftDiffersFrom(companyConfig);

  // Walkthrough Previous/Next save first; every other way out prompts while edits are unsaved.
  const leaveGuard = useLeaveGuard(isDirty);

  const handleCopyFromGpcApproach = () => {
    const approach = gpcApproaches.find(candidate => candidate.gpc_approach_id === selectedGpcApproachId);
    if (!approach) {
      return;
    }
    // Adds rather than replaces: the wizard directs the user to choose, not overwrite.
    addComps(gpcApproachToCompRows(approach));
  };

  const handleAddCompsFromDialog = (data: CapIQCompanyData[]) => addComps(capIqSelectionToCompRows(data));

  // No date of its own: search as of today, not AddGpcDialog's fallback (the firm's oldest date).
  const compSearchMarketDate = searchDate?.measurement_date ?? dbShortDate() ?? undefined;
  const gpcDialogMeasurementDate = useMemo<CurrentCompanyMeasurementDate>(
    () => ({
      id: searchDate?.company_measurement_date_id ?? null,
      name: searchDate ? dailyNavDateLabel(searchDate.measurement_date) : 'Today',
      slug: '',
      date: compSearchMarketDate ?? '',
      is_open: false,
      md_id: searchDate?.measurement_date_id ?? null,
      company_id: companyId,
      company_name: company.company_name ?? '',
      is_ready: true,
    }),
    [searchDate, compSearchMarketDate, companyId, company.company_name]
  );

  const saveThen = async (next: () => void) => {
    // Never write a draft that was not seeded from the saved config, and wait for comp
    // lookups so the save carries their resolved ids.
    if (!currentConfig || isResolvingLinks) {
      return;
    }
    if (!isDirty) {
      next();
      return;
    }
    if (!isOverridesValid) {
      return;
    }
    // The saved config replaces the draft once refetched; a failed save keeps protecting it.
    reseedNextRef.current = true;
    const updated = await updateCompanyDailyNavConfig(firmId, companyId, {
      ...overridesToPayload(overrides),
      comps,
      alexandria_id: alexandriaId,
      monitoring_enabled: monitoringEnabled,
    });
    if (!updated) {
      reseedNextRef.current = false;
    }
    if (updated) {
      enqueueSnackbar(`${company.company_name} saved`, { variant: 'success' });
      onConfigSaved?.(companyConfig, updated);
      leaveGuard.allowNextNavigation();
      next();
    }
  };

  const walkIndex = walkthrough?.companies.findIndex(row => row.company_id === companyId) ?? -1;
  const previousCompany = walkthrough && walkIndex > 0 ? walkthrough.companies[walkIndex - 1] : null;
  const nextCompany = walkthrough?.companies[walkIndex + 1] ?? null;
  const navigateTo = (targetId: number) => saveThen(() => walkthrough?.onNavigate(targetId));
  let nextLabel = nextCompany ? 'Next company' : 'Finish';
  if (isDirty) {
    nextLabel = nextCompany ? 'Save & next company' : 'Save & finish';
  }

  if (isLoading) {
    return <GridSkeleton />;
  }

  if (!currentConfig) {
    return (
      <Stack spacing={3} data-testid={DAILY_NAV_SETUP_TEST_IDS.companySetup(companyId)}>
        <Alert
          severity="error"
          data-testid={DAILY_NAV_SETUP_TEST_IDS.companyConfigErrorAlert}
          action={
            <Button size="small" onClick={() => refetchConfig()} data-testid={DAILY_NAV_SETUP_TEST_IDS.companyConfigRetryButton}>
              Retry
            </Button>
          }>
          {company.company_name}&apos;s Daily NAV settings couldn&apos;t be loaded, so they can&apos;t be edited here
          yet.
        </Alert>
        <Stack direction="row">
          <Button onClick={onBack} data-testid={DAILY_NAV_SETUP_TEST_IDS.backToCompaniesButton}>
            Back to companies
          </Button>
        </Stack>
      </Stack>
    );
  }

  const selectedApproach = gpcApproaches.find(approach => approach.gpc_approach_id === selectedGpcApproachId);
  const needsComps = monitoringEnabled && !comps.length;
  // Saves and moves wait while added comps are still being checked against Alexandria.
  const isBusy = isPending || isResolvingLinks;
  // Spin only the button that started the save, not every button it disables.
  const isSavingVia = (trigger: 'previous' | 'primary') => isPending && saveTrigger === trigger;

  return (
    <Stack spacing={3} data-testid={DAILY_NAV_SETUP_TEST_IDS.companySetup(companyId)}>
      <LeaveWithUnsavedChangesDialog
        open={leaveGuard.isLeavePending}
        body={`Your changes to ${company.company_name} haven't been saved.`}
        isSaving={isBusy}
        onKeepEditing={leaveGuard.cancelLeave}
        onDiscard={leaveGuard.confirmLeave}
        onSaveAndLeave={
          isOverridesValid && !disabled ? () => saveThen(leaveGuard.confirmLeave) : undefined
        }
      />
      {walkthrough && (
        <CompanyWalkthroughProgress companies={walkthrough.companies} activeCompanyId={companyId} />
      )}
      <div>
        <Text variant="h4" gutterBottom>
          {company.company_name}
        </Text>
        <Text variant="body2" color="text.secondary">
          {walkthrough
            ? 'Your changes save when you move to another company.'
            : 'Nothing here is saved until you press Save.'}
        </Text>
      </div>

      <Section
        number={1}
        title="Include in Daily NAV"
        isPending={false}
        isDone={monitoringEnabled}
        testId={DAILY_NAV_SETUP_TEST_IDS.includeSection}>
        <FormControlLabel
          control={
            <Switch
              checked={monitoringEnabled}
              onChange={event => setMonitoringEnabled(event.target.checked)}
              disabled={disabled}
              id={DAILY_NAV_SETUP_TEST_IDS.includeSwitch}
            />
          }
          label={monitoringEnabled ? 'Included in daily generation' : 'Excluded: no daily record will be generated'}
        />
      </Section>

      {monitoringEnabled && (
        <>
          <Section
            number={2}
            title="Confirm the external company profile"
            isPending={!isAlexandriaConfirmed}
            isDone={isAlexandriaConfirmed}
            testId={DAILY_NAV_SETUP_TEST_IDS.alexandriaSection}>
            {!alexandriaId && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Choose the profile that is {company.company_name} from the suggestions below. If none match, search a
                different name, such as its legal name.
              </Alert>
            )}
            {alexandriaId && !isAlexandriaConfirmed && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Check this is the right company, then Save to confirm the link.
              </Alert>
            )}
            <AlexandriaCompanyPicker
              firmId={firmId}
              companyId={companyId}
              selectedAlexandriaId={alexandriaId}
              onChange={setAlexandriaId}
              disabled={disabled}
            />
          </Section>

          <Section
            number={3}
            title="Choose comps"
            isPending={needsComps}
            isDone={!needsComps}
            testId={DAILY_NAV_SETUP_TEST_IDS.compsSection}>
            <Text variant="body2" sx={{ mb: 2 }}>
              The public comps and ETFs whose price moves drive this company&apos;s deltas. Optional, but a company
              with no comps shows no deltas.
            </Text>
            {setupState === 'loading' && (
              <Skeleton variant="text" width={260} height={20} sx={{ mb: 2 }} data-testid={DAILY_NAV_SETUP_TEST_IDS.setupLoadingIndicator} />
            )}
            {setupState === 'error' && (
              <Alert severity="error" sx={{ mb: 2 }} data-testid={DAILY_NAV_SETUP_TEST_IDS.setupErrorAlert}>
                {setupErrorMessage ??
                  'This company\'s previous valuation and GPC comps couldn\'t be loaded. Refresh the page to try again.'}
              </Alert>
            )}
            {setupState === 'ready' && previousValuation && (
              <Text variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Previous valuation: final as of {dailyNavDateLabel(previousValuation.measurement_date)}.
              </Text>
            )}
            {setupState === 'ready' && !previousValuation && (
              <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
                {company.company_name} has no final valuation yet, so there are no GPC comps to start from and Daily
                NAV has no previous valuation to compare against.
                {searchDate
                  ? ` Search Cap IQ below; it searches as of ${dailyNavDateLabel(searchDate.measurement_date)}.`
                  : ' Search Cap IQ below to add comps; it searches as of today.'}
              </Alert>
            )}
            {gpcApproaches.length > 0 && (
              <Box
                sx={{ p: 2, mb: 2, borderRadius: 1, bgcolor: needsComps ? 'action.hover' : 'transparent' }}
                data-testid={DAILY_NAV_SETUP_TEST_IDS.gpcPanel}>
                <Text variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                  Start from the comps in this valuation&apos;s GPC approach
                </Text>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                  <Select
                    size="small"
                    displayEmpty
                    value={selectedGpcApproachId}
                    onChange={event =>
                      setSelectedGpcApproachId(event.target.value === '' ? '' : Number(event.target.value))
                    }
                    disabled={disabled}
                    data-testid={DAILY_NAV_SETUP_TEST_IDS.gpcApproachSelect}
                    sx={{ minWidth: 300 }}>
                    <MenuItem value="">
                      <em>Select a GPC approach</em>
                    </MenuItem>
                    {gpcApproaches.map((approach, index) => (
                      <MenuItem key={approach.gpc_approach_id} value={approach.gpc_approach_id}>
                        GPC Approach {index + 1} · {approach.comps.length} comp{approach.comps.length === 1 ? '' : 's'}
                      </MenuItem>
                    ))}
                  </Select>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleCopyFromGpcApproach}
                    disabled={disabled || !selectedApproach}
                    data-testid={DAILY_NAV_SETUP_TEST_IDS.copyGpcButton}>
                    Add these comps
                  </Button>
                </Stack>
                {selectedApproach && (
                  <Text variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {selectedApproach.comps.map(comp => comp.ticker_symbol || comp.company_name).join(', ')}
                  </Text>
                )}
              </Box>
            )}
            <Box sx={{ mb: 2 }}>
              <AddGpcDialog
                buttonLabel={ADD_COMP_OR_ETF_BUTTON_LABEL}
                currentComps={comps.map(comp => ({
                  cap_iq_id: comp.cap_iq_id,
                  ticker_symbol: comp.ticker_symbol,
                  company_name: comp.company_name,
                }))}
                dialogTitle={ADD_PUBLIC_OR_ETF_DIALOG_TITLE}
                onAdd={handleAddCompsFromDialog}
                openGpcDialog={false}
                disabled={disabled}
                firmId={firmId}
                currentMeasurementDate={gpcDialogMeasurementDate}
                currentMarketDate={compSearchMarketDate}
              />
            </Box>
            {comps.length ? (
              <Table size="small" data-testid={DAILY_NAV_SETUP_TEST_IDS.compsTable}>
                <TableHead>
                  <TableRow>
                    <TableCell>Ticker</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Remove</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comps.map(comp => (
                    <TableRow key={comp.cap_iq_id}>
                      <TableCell>{comp.ticker_symbol}</TableCell>
                      <TableCell>{comp.company_name}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={COMP_TYPE_LABELS[comp.comp_type]} />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          tooltipLabel="Remove"
                          aria-label={`Remove ${comp.company_name}`}
                          data-testid={DAILY_NAV_SETUP_TEST_IDS.removeCompButton(comp.cap_iq_id)}
                          onClick={() => removeComp(comp.cap_iq_id)}
                          disabled={disabled}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Text variant="body2" color="text.secondary">
                No comps added yet.
              </Text>
            )}
          </Section>

          <Accordion variant="outlined" disableGutters data-testid={DAILY_NAV_SETUP_TEST_IDS.overridesSection}>
            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Text variant="h4">4. Alert threshold overrides</Text>
                <Chip
                  size="small"
                  label={
                    overrideCount
                      ? `${overrideCount} override${overrideCount === 1 ? '' : 's'}`
                      : 'Using firm defaults'
                  }
                  color={overrideCount ? 'primary' : 'default'}
                  variant={overrideCount ? 'filled' : 'outlined'}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 3, pb: 3 }}>
              <Text variant="body2" sx={{ mb: 2 }}>
                Optional. Leave a field empty to use the firm default shown in it; any value, including 0%, applies to
                this company only.
              </Text>
              {thresholdGroups.map(group => (
                <Box key={group.title} sx={{ mb: 2 }}>
                  <Text variant="caption" fontWeight="bold" color="text.secondary">
                    {group.title.toUpperCase()}
                  </Text>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {group.fields.map(field => {
                      const isOverridden = overrides[field.key] !== '';
                      const firmDefault = fractionToPercent(firmConfig?.[field.key]);
                      return (
                        <Grid key={field.key} size={{ xs: 12, sm: 4 }}>
                          <Input
                            fullWidth
                            size="small"
                            label={field.label}
                            value={overrides[field.key]}
                            placeholder={firmDefault ? `${firmDefault} (firm default)` : 'Firm default'}
                            onChange={event =>
                              setOverrides(current => ({ ...current, [field.key]: event.target.value }))
                            }
                            error={isOverridden && !isValidThresholdPercent(overrides[field.key])}
                            disabled={disabled}
                            slotProps={{
                              htmlInput: { 'data-testid': DAILY_NAV_SETUP_TEST_IDS.overrideInput(field.key) },
                              inputLabel: { shrink: true },
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    %
                                    {isOverridden && (
                                      <IconButton
                                        size="small"
                                        edge="end"
                                        tooltipLabel="Reset to firm default"
                                        aria-label={`Reset ${field.label} to firm default`}
                                        data-testid={DAILY_NAV_SETUP_TEST_IDS.resetOverrideButton(field.key)}
                                        onClick={() => setOverrides(current => ({ ...current, [field.key]: '' }))}
                                        disabled={disabled}>
                                        <RestartAlt fontSize="small" />
                                      </IconButton>
                                    )}
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {isResolvingLinks && (
        <Text variant="body2" color="text.secondary" data-testid={DAILY_NAV_SETUP_TEST_IDS.checkingCompsNote}>
          Checking comps against Alexandria… You can save once this finishes.
        </Text>
      )}
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Button onClick={onBack} data-testid={DAILY_NAV_SETUP_TEST_IDS.backToCompaniesButton}>
          Back to companies
        </Button>
        {walkthrough ? (
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => {
                setSaveTrigger('previous');
                if (previousCompany) {
                  navigateTo(previousCompany.company_id);
                }
              }}
              disabled={disabled || isBusy || !isOverridesValid || !previousCompany}
              loading={isSavingVia('previous')}
              data-testid={DAILY_NAV_SETUP_TEST_IDS.previousCompanyButton}>
              {isDirty ? 'Save & previous company' : 'Previous company'}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setSaveTrigger('primary');
                if (nextCompany) {
                  navigateTo(nextCompany.company_id);
                } else {
                  saveThen(walkthrough.onFinish);
                }
              }}
              disabled={disabled || isBusy || !isOverridesValid}
              loading={isSavingVia('primary')}
              data-testid={DAILY_NAV_SETUP_TEST_IDS.saveCompanyButton}>
              {nextLabel}
            </Button>
          </Stack>
        ) : (
          <Button
            variant="contained"
            onClick={() => {
              setSaveTrigger('primary');
              saveThen(onSaved);
            }}
            disabled={disabled || isBusy || !isOverridesValid}
            loading={isSavingVia('primary')}
            data-testid={DAILY_NAV_SETUP_TEST_IDS.saveCompanyButton}>
            {isDirty ? 'Save' : 'Done'}
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

export default CompanySetupStep;
